import { closestMarketData } from 'client/javascript/get_closest_market';
import * as storage from 'client/javascript/storage';
import { getSearchWidgetInstance } from 'client/javascript/pages/shared/sections/hero_container/search_widget';
import { logError } from 'client/javascript/util/logger';
import { TEAMS } from 'shared/rollbar/global_prefixes';
import { getGeogroupingSlugFromPath } from 'shared/util/geogroupings';
import { isMobileBreakpoint } from 'client/javascript/util/device_breakpoints';

export const LOGGING_CONFIG = {
  prefix: '[SearchWidgetLocationFilterUI]',
  team: TEAMS.MARKETING
};

export const FILTER_NAME = 'location';
const FILTER_SEARCH_PARAMETER = 'slug';
const ENTER_KEY_NAME = 'Enter';

export const CLASSES = {
  MODULE_LIST_ITEM_ACTIVE: 'location-filter-list__item--active'
};

export const SELECTORS = {
  MODULE: '.location-filter',
  MODULE_DROPDOWN: '[data-dropdown-name="location"]',
  MODULE_FILTER_ITEM: '.location-filter__item',
  MODULE_FILTER_LIST_CONTAINER: '.location-filter-list__container',
  MODULE_FILTER_LIST: '.location-filter__list',
  MODULE_FILTER_LIST_ITEM: '.location-filter-list__item',
  MODULE_ITEM_ACCORDION_LABEL: '.location-filter__accordion-label',
  MODULE_LIST_ITEM_ACTIVE: `.${CLASSES.MODULE_LIST_ITEM_ACTIVE}`
};

export async function setupLocationFilter(containerDomNode) {
  try {
    const dropdownDomNode = containerDomNode.querySelector(SELECTORS.MODULE_DROPDOWN);
    const { closestMarketId } = await getLocationId(dropdownDomNode);

    const marketDomNode = dropdownDomNode.querySelector(`[data-market-id="${closestMarketId}"]`);

    if (!marketDomNode) {
      return;
    }

    const searchWidgetInstance = getSearchWidgetInstance();

    setDefaultFilterData({ searchWidgetInstance, marketDomNode });
    setActiveRegion({ dropdownDomNode, marketDomNode });
    initEnterKeyPressFilter({ dropdownDomNode, searchWidgetInstance });
    initFilterListItems(dropdownDomNode);

    if (isMobileBreakpoint()) {
      setupScrollToMarket({
        containerDomNode,
        marketDomNode,
        dropdownDomNode
      });
      setupMarketsListScrollEffect(dropdownDomNode);
    }
  } catch (error) {
    logError(`${LOGGING_CONFIG.prefix}[setupLocationFilter()] failed to setup region list items`, error, {
      team: LOGGING_CONFIG.team
    });
  }
}

function setDefaultFilterData({ searchWidgetInstance, marketDomNode }) {
  const { marketPath } = marketDomNode.dataset;
  const marketSlug = getGeogroupingSlugFromPath(marketPath);

  searchWidgetInstance.setFilterValue(FILTER_NAME, marketDomNode.textContent);
  searchWidgetInstance.updateSearchUrl(FILTER_SEARCH_PARAMETER, marketSlug);
}

function setActiveRegion({ dropdownDomNode, marketDomNode }) {
  marketDomNode.classList.add(CLASSES.MODULE_LIST_ITEM_ACTIVE);

  const listItemDomNode = marketDomNode.closest(SELECTORS.MODULE_FILTER_ITEM);
  const firstListItemDomNode = dropdownDomNode.querySelector(`${SELECTORS.MODULE} ${SELECTORS.MODULE_FILTER_ITEM}`);

  if (firstListItemDomNode !== listItemDomNode) {
    insertActiveRegionAsFirstItem(listItemDomNode);
  }

  selectRegionItem(dropdownDomNode, listItemDomNode);
}

function initEnterKeyPressFilter({ dropdownDomNode, searchWidgetInstance }) {
  const filterItems = Array.from(dropdownDomNode.querySelectorAll(SELECTORS.MODULE_FILTER_ITEM));
  const lastFilterItem = filterItems[filterItems.length - 1];

  filterItems.forEach((itemDomNode) => {
    itemDomNode.addEventListener('keypress', (event) => {
      if (event.key === ENTER_KEY_NAME) {
        selectRegionItem(dropdownDomNode, itemDomNode);

        // Set the last element for focus area of Search Widget because by default the element is the last region item.
        if (itemDomNode === lastFilterItem) {
          const lastListItemDomNode = Array.from(
            dropdownDomNode.querySelectorAll(`${SELECTORS.MODULE} ${SELECTORS.MODULE_FILTER_LIST_ITEM}`)
          ).pop();

          searchWidgetInstance.updateFocusArea('lastElement', lastListItemDomNode);
        }
      }
    });
  });
}

function selectRegionItem(dropdownDomNode, itemDomNode) {
  const accordionLabelDomNode = itemDomNode.querySelector(SELECTORS.MODULE_ITEM_ACCORDION_LABEL);
  const accordionId = accordionLabelDomNode.getAttribute('for');
  const accordionInputDomNode = dropdownDomNode.querySelector(`#${accordionId}`);
  accordionInputDomNode.checked = true;
}

function insertActiveRegionAsFirstItem(itemDomNode) {
  const { parentNode, previousElementSibling } = itemDomNode;
  parentNode.insertBefore(itemDomNode, parentNode.firstElementChild);
  parentNode.insertBefore(previousElementSibling, parentNode.firstElementChild);
}

function initFilterListItems(dropdownDomNode) {
  dropdownDomNode.querySelectorAll(SELECTORS.MODULE_FILTER_LIST_ITEM).forEach((domNode) => {
    domNode.addEventListener('click', setupLocation);
    domNode.addEventListener('keypress', (event) => {
      if (event.key === ENTER_KEY_NAME) {
        setupLocation(event);
      }
    });
  });
}

function setupMarketsListScrollEffect(dropdownDomNode) {
  dropdownDomNode.querySelectorAll(SELECTORS.MODULE_FILTER_LIST_CONTAINER).forEach((domNode) => {
    const listDomNode = domNode.querySelector(SELECTORS.MODULE_FILTER_LIST);
    const observerOptions = {
      root: listDomNode,
      threshold: 1
    };

    const observer = new IntersectionObserver((entries) => {
      const [interactedDomNode] = entries;

      const isElementFirstChild = !interactedDomNode.target.previousElementSibling;
      const isElementLastChild = !interactedDomNode.target.nextElementSibling;

      if (interactedDomNode.isIntersecting) {
        if (isElementFirstChild) {
          domNode.firstElementChild.classList.remove('show-up');
        } else if (isElementLastChild) {
          domNode.lastElementChild.classList.remove('show-up');
        }
      } else if (isElementFirstChild) {
        domNode.firstElementChild.classList.add('show-up');
      } else if (isElementLastChild) {
        domNode.lastElementChild.classList.add('show-up');
      }
    }, observerOptions);

    observer.observe(listDomNode.lastElementChild);
    observer.observe(listDomNode.firstElementChild);
  });
}

function setupScrollToMarket({ containerDomNode, marketDomNode, dropdownDomNode }) {
  const observerOptions = {
    root: containerDomNode
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const isVisible = entry.intersectionRatio > 0;

      if (isVisible) {
        const { offsetTop } = marketDomNode;
        const { height: parentHeight } = marketDomNode.parentNode.getBoundingClientRect();

        const scrollHeightToMiddle = offsetTop - parentHeight + parentHeight / 2;

        if (scrollHeightToMiddle > 0) {
          marketDomNode.parentNode.scrollTo({
            top: scrollHeightToMiddle,
            behavior: 'smooth'
          });
        }
      }
    });
  }, observerOptions);

  observer.observe(dropdownDomNode);
}

function setupLocation({ target }) {
  const { marketPath, marketId } = target.dataset;
  const marketSlug = getGeogroupingSlugFromPath(marketPath);

  if (!target.classList.contains(CLASSES.MODULE_LIST_ITEM_ACTIVE)) {
    target
      .closest(SELECTORS.MODULE_DROPDOWN)
      .querySelector(SELECTORS.MODULE_LIST_ITEM_ACTIVE)
      .classList.remove(CLASSES.MODULE_LIST_ITEM_ACTIVE);
    target.classList.add(CLASSES.MODULE_LIST_ITEM_ACTIVE);
  }

  storage.setLocationId(marketId);
  const searchWidgetInstance = getSearchWidgetInstance();
  window.location = searchWidgetInstance.updateSearchUrl(FILTER_SEARCH_PARAMETER, marketSlug);
}

async function getLocationId() {
  const locationIdFromLocalStorage = storage.getLocationId();
  const { id: closestMarketId } = await closestMarketData;

  return {
    closestMarketId: locationIdFromLocalStorage || closestMarketId
  };
}
